import { useEffect, useState } from "react";

/**
 * `beforeinstallprompt` capture — only Chromium-based browsers fire this.
 * iOS Safari requires "Add to Home Screen" from the share sheet; we
 * expose `isIOS` + `isStandalone` so the UI can show the right hint.
 *
 * Usage:
 *   const { isInstallable, promptInstall, isIOS, isStandalone } = useInstallPrompt();
 *   {isInstallable && <button onClick={promptInstall}>Install</button>}
 */
export default function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

  async function promptInstall() {
    if (!deferred) return { outcome: "unavailable" };
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice; // { outcome: 'accepted' | 'dismissed', platform }
    } catch {
      return { outcome: "error" };
    }
  }

  return {
    isInstallable: !!deferred && !isStandalone,
    promptInstall,
    isIOS,
    isStandalone,
    installed,
  };
}
